// Behaviour artifacts, persisted per tenant.
//
// The executable tier beside the declarative runtime lenses: a behaviour whose
// implementation is Python source with authority over a trace's turn windows,
// bound at read time by the emission contract rather than by a spec grammar.
// Same localStorage posture as xrayRuntimeLensStore and for the same reason —
// prove the mount point in one sitting; the move to the backend (and to batch
// materialization) is a transport change, not a rewrite.
//
// The stored record is source + contract manifest and nothing derived. The
// version is NOT stored: it is recomputed from content on read, so an edited
// artifact can never keep its predecessor's identity.

import { browserStorageKey, discardLegacyTenantEntry } from "./browserStorageScope.js";
import { contentVersion, validateBehaviourManifest } from "../../xray-factory/src/emit/contract.js";

const STORAGE_KEY_PREFIX = "origin.xray.behaviourArtifacts.v2";
const LEGACY_STORAGE_KEY_PREFIX = "origin.xray.behaviourArtifacts.v1";

let _storageOverride = null;
function storage() {
  if (_storageOverride) return _storageOverride;
  return typeof localStorage === "undefined" ? null : localStorage;
}

/** Test-only: supply a storage backend. Pass null to fall back to the global. */
export function _setStorageForTests(s) { _storageOverride = s; }

function storageKey(tenantId) {
  discardLegacyTenantEntry(storage(), LEGACY_STORAGE_KEY_PREFIX, tenantId);
  return browserStorageKey(STORAGE_KEY_PREFIX, tenantId);
}

const listeners = new Set();
function notify() {
  for (const fn of listeners) {
    try { fn(); } catch { /* one listener's failure must not block its siblings */ }
  }
}

/** Subscribe to artifact mounts/unmounts. Returns an unsubscribe. */
export function subscribeBehaviourArtifacts(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * The version an artifact record resolves to — content-derived, never stored.
 */
export function artifactVersion(artifact) {
  return contentVersion(`${artifact?.source ?? ""}\n${JSON.stringify(artifact?.manifest ?? null)}`);
}

/**
 * The full contract manifest an artifact record implies: the stored manifest
 * plus the identity fields the store owns (behaviour id, content version).
 */
export function manifestOf(artifact) {
  return {
    ...(artifact?.manifest && typeof artifact.manifest === "object" ? artifact.manifest : {}),
    behaviour: artifact?.behaviour,
    version: artifactVersion(artifact),
  };
}

/** Every stored artifact for a tenant, newest first. Never throws. */
export function listBehaviourArtifacts(tenantId) {
  try {
    const key = storageKey(tenantId);
    if (!key) return [];
    const raw = storage()?.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((a) => a && typeof a.behaviour === "string" && typeof a.source === "string");
  } catch {
    return [];
  }
}

/**
 * Mount an artifact, replacing any with the same behaviour id.
 *
 * Validated against the CONTRACT before writing — not against the source,
 * which only execution can judge. An artifact whose manifest cannot index into
 * the graph is refused while its author still has it in front of them.
 *
 * @returns {{ok, problems: string[]}}
 */
export function saveBehaviourArtifact(tenantId, artifact) {
  const key = storageKey(tenantId);
  if (!key) return { ok: false, problems: ["verified tenant and user identity are required"] };
  const problems = [];
  const source = String(artifact?.source || "");
  if (!source.trim()) problems.push("source: required — an artifact with no implementation observes nothing");
  if (!/def\s+emit\s*\(/.test(source)) {
    problems.push("source: must define `emit(window)` — the executor's single entry point");
  }
  const check = validateBehaviourManifest(manifestOf(artifact));
  problems.push(...check.problems);
  if (problems.length) return { ok: false, problems };

  const kept = listBehaviourArtifacts(tenantId).filter((a) => a.behaviour !== artifact.behaviour);
  try {
    storage()?.setItem(key, JSON.stringify([
      {
        behaviour: artifact.behaviour,
        source,
        manifest: artifact.manifest,
        draftedFrom: artifact.draftedFrom ?? null,
        savedAt: Date.now(),
      },
      ...kept,
    ]));
  } catch (err) {
    return { ok: false, problems: [`could not persist: ${err?.message || String(err)}`] };
  }
  notify();
  return { ok: true, problems: [] };
}

/** Unmount one artifact. Returns whether it was there. */
export function deleteBehaviourArtifact(tenantId, behaviour) {
  const key = storageKey(tenantId);
  if (!key) return false;
  const all = listBehaviourArtifacts(tenantId);
  const kept = all.filter((a) => a.behaviour !== behaviour);
  if (kept.length === all.length) return false;
  try {
    storage()?.setItem(key, JSON.stringify(kept));
  } catch {
    return false;
  }
  notify();
  return true;
}

/** Test-only: drop a tenant's bucket. */
export function _resetForTests(tenantId) {
  try { const key = storageKey(tenantId); if (key) storage()?.removeItem(key); } catch { /* noop */ }
  listeners.clear();
}
